import { DestroyRef, effect, inject, signal, untracked, WritableSignal } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { DetailPanelService } from '@connect-services/client/services/detail-panel.service';
import { LayerService } from '@connect-services/client/services/layer.service';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import { SortState } from '@connect-services/src/lib/components/table/table.component';
import { Layer } from '@connect-services/src/sdk/api';
import { defaultPaginationParameters, OrderEnum } from '@shared/client/models/pagination.model';

export abstract class AbstractLayersTableComponent {
    public readonly currentPanelEntity: WritableSignal<string | null> = signal(null);
    public readonly isLoading = signal(false);
    public readonly layers: WritableSignal<Layer[]> = signal([]);
    public LayerTypeEnum = Layer.TypeEnum;
    public readonly paginationParameters = signal({
        ...defaultPaginationParameters,
        order: OrderEnum.DESC,
        orderBy: 'order',
        take: 5,
    });

    public paginator: PaginatorComponent | undefined;

    protected readonly detailPanelService = inject(DetailPanelService);
    protected readonly layerService = inject(LayerService);
    protected readonly projectBloc = inject(ProjectBloc);

    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private readonly subscription: Subscription;

    protected constructor() {
        this.destroyRef.onDestroy(() => {
            this.subscription.unsubscribe();
        });

        this.currentPanelEntity.set(this.detailPanelService.getDetailPanelUuidFromUrl());

        this.subscription = this.router.events
            .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((): void => {
                this.currentPanelEntity.set(this.detailPanelService.getDetailPanelUuidFromUrl());
            });

        effect(() => {
            if (!this.projectBloc.value()) {
                return;
            }

            untracked(() => this.resetPaginationParams());
        });
    }

    protected async loadLayers(): Promise<void> {}

    protected async onPageChange(page: number): Promise<void> {
        this.paginationParameters.update((parameters) => {
            return { ...parameters, page: page };
        });

        await this.loadLayers();
    }

    protected onPaginatorChange(paginator: PaginatorComponent): void {
        this.paginator = paginator;
    }

    protected resetPaginationParams(): void {
        this.paginationParameters.set({
            ...defaultPaginationParameters,
            order: OrderEnum.DESC,
            orderBy: 'order',
            take: 5,
        });

        this.paginator?.resetPaginator();
    }

    protected async sort(sortState: SortState | undefined): Promise<void> {
        const order = sortState?.order ?? OrderEnum.DESC;
        const orderBy = sortState?.orderBy ?? 'order';

        this.paginationParameters.set({
            ...this.paginationParameters(),
            order: order,
            orderBy: orderBy,
        });

        await this.loadLayers();
    }
}
